import React from 'react';
import { useState, useEffect, useRef } from 'react';
import ReadableTime from './ReadableTime';

/**
 * Renders a string showing how much time remains until a given future date.
 * The component will refresh itself every second by default, and will stop when the time expires.
 * @param {Date} props.until - The future date to count down to
 * @param {number} props.refreshInterval - The interval in milliseconds to refresh the timer (default: 1000)
 * @param {Function} props.onExpires - Callback function fired when the countdown reaches zero
 * @return {ReactElement} The remaining time as a string
 */
const TimeRemaining = (props) => {
  const [seconds, setSeconds] = useState(0);
  const [targetTime] = useState(props.until);
  const intervalRef = useRef(null);
  const hasExpiredRef = useRef(false);

  const timerRefreshInterval = props.refreshInterval || 1000;

  useEffect(() => {
    const updateCountdown = () => {
      const targetDate = new Date(targetTime).getTime();
      const currentTime = new Date().getTime();
      const _seconds = Math.max(0, (targetDate - currentTime) / 1000);

      setSeconds(_seconds);

      if (_seconds <= 0) {
        clearInterval(intervalRef.current);

        if (!hasExpiredRef.current && props.onExpires) {
          hasExpiredRef.current = true;
          props.onExpires();
        }
      }
    };

    updateCountdown();

    intervalRef.current = setInterval(updateCountdown, timerRefreshInterval);

    return () => clearInterval(intervalRef.current);
  }, [targetTime, timerRefreshInterval]);

  return <ReadableTime seconds={seconds} />;
};

export default TimeRemaining;
